import {
  CheckCircleFilled,
  FilterOutlined,
  PlusOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Tooltip, Button, Badge } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import GetFilteredPerfCyclesApi from "../../api/GetFilteredPerfCyclesApi";
import GetPerfCyclesApi from "../../api/GetPerfCyclesApi";
import { SetupStatusApi } from "../../api/SetupStatusApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import AddPerfCycleModal from "../../components/modal/add/AddPerfCycleModal";
import PerfCycleFilterModal from "../../components/modal/filters/PerfCycleFilterModal";
import PageLoader from "../../components/sub/PageLoader";
import PerfCycleTable from "../../components/tables/PerfCyclesTable";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function PerformanceCycles() {
  const [canStart, setCanStart] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canShowTable, setCanShowTable] = useState(false);
  const [data, setData] = useState();
  const [addModal, setAddModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(false);
  const [filterBy, setFilterBy] = useState();
  const [page, setPage] = useState(1);
  const proHeight = document.body.clientHeight;
  let navigate = useNavigate();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    getSetupProgress();
  }, []);

  async function getSetupProgress() {
    const apiResponse = await SetupStatusApi();
    if (apiResponse.success) {
      if (apiResponse.data?.planTemplates) {
        setCanAdd(true);
        getPerfCycles(1);
      } else {
        setCanStart(true);
      }
    }
  }

  async function getPerfCycles(num) {
    setTableLoading(true);
    const apiResponse = await GetPerfCyclesApi(num, LIMIT_PER_PAGE);
    if (apiResponse.message === "success") {
      if (apiResponse.performanceCycles?.length) {
        /*To show add component */
        !canShowTable && setCanShowTable(true);
      }
      setData(apiResponse);
      setTableLoading(false);
      setPage(num);
      setCanStart(true);
    }
  }

  async function getFilteredPerfCycles(values, num) {
    setTableLoading(true);
    const apiResponse = await GetFilteredPerfCyclesApi(values.cycleName, num, LIMIT_PER_PAGE);
    if (apiResponse.message === "success") {
      setData(apiResponse);
      setTableLoading(false);
      setFilterStatus(true);
      setFilterBy({ cycleName: values.cycleName });
    }
  }

  function changeTablePage(num) {
    if (filterStatus) {
      getFilteredPerfCycles(filterBy, num);
    } else {
      getPerfCycles(num);
    }
  }

  function resetFilter() {
    getPerfCycles(1);
    setFilterStatus(false);
    setFilterBy();
  }

  const right = (
    <>
      {canShowTable && (
        <>
          <Tooltip title={"Reset"}>
            <Button
              disabled={!filterStatus}
              onClick={() => resetFilter()}
              className="mr-3"
            >
              <UndoOutlined />
            </Button>
          </Tooltip>
          <Badge
            count={
              filterStatus ? (
                <CheckCircleFilled style={{ color: "#f5222d" }} />
              ) : (
                0
              )
            }
          >
            <Tooltip title={"Filter"}>
              <Button onClick={() => setFilterModal(true)}>
                <FilterOutlined />
              </Button>
            </Tooltip>
          </Badge>
        </>
      )}
      {permissionAbility(userLevel, "perf-cycles", "add") && (
        <Tooltip placement="left" title={"Add cycle"}>
          <Button
            className="ml-3"
            onClick={() => setAddModal(true)}
            shape="circle"
            type="primary"
            icon={<PlusOutlined />}
          ></Button>
        </Tooltip>
      )}
    </>
  );

  const left = <div className="pri-font text-lg">Performance Cycles</div>;

  function showEmptyCycles() {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No performance cycle available
          </div>
          {permissionAbility(userLevel, "perf-cycles", "add") && (
            <>
              <div className="mb-3 text-gray-400">
                Add your first performance cycle for the organization
              </div>
              <Button onClick={() => setAddModal(true)} type="primary">
                Add a cycle
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  function showAddPlanTemplates() {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            Add plan templates
          </div>
          <div className="mb-3 text-gray-400">
            You must add plan templates before adding perf cycles
          </div>
          <Button
            onClick={() => navigate("/setup-account/general/plan-templates")}
            type="primary"
          >
            Add plan templates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} right={right} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll p-6"
        >
          {canStart ? (
            canAdd ? (
              canShowTable ? (
                <PerfCycleTable
                  data={data}
                  changePage={(num) => changeTablePage(num)}
                  loading={tableLoading}
                  page={page}
                />
              ) : (
                showEmptyCycles()
              )
            ) : (
              showAddPlanTemplates()
            )
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
      <AddPerfCycleModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => getPerfCycles(1)}
      />
      <PerfCycleFilterModal
        visible={filterModal}
        handleCancel={() => setFilterModal(false)}
        handleOk={(values) => getFilteredPerfCycles(values, 1)}
        filterBy={filterBy}
      />
    </>
  );
}

export default PerformanceCycles;
